import { useEffect, useMemo, useState } from 'react';
import { suggestionsApi, type AiSuggestion } from '../../api/suggestions';
import type { EntrySeverity } from '../../api/deficiency-entries';
import { Icon } from '../templates/icons';

export interface SuggestionSelection {
  title: string;
  description: string | null;
  severity: EntrySeverity;
}

interface Props {
  /** Template section name the active room maps to — room.name in practice. */
  sectionName: string;
  /**
   * Required — suggestions are scoped to a specific component. The strip
   * renders nothing when omitted.
   */
  componentName: string;
  /**
   * Titles of findings already added in this scope — excluded server-side so
   * applied items don't re-appear in the strip.
   */
  existingFindingTitles?: string[];
  /**
   * Bumped by the parent after a library write (e.g. SaveToLibrarySheet's
   * onSaved) to force a refetch — otherwise the strip caches stale suggestions
   * and a freshly-saved `user_curated` entry won't appear until the inspector
   * navigates away and back. (#44)
   */
  refreshKey?: number;
  /** Fires with the selected suggestion data so the parent pre-fills its form. */
  onSuggestionSelected: (suggestion: SuggestionSelection) => void;
  onError?: (message: string) => void;
}

const COLLAPSED_COUNT = 3;
const EXPANDED_MAX = 12;

export function SuggestionsStrip({
  sectionName,
  componentName,
  existingFindingTitles,
  refreshKey,
  onSuggestionSelected,
  onError,
}: Props) {
  const [rows, setRows] = useState<AiSuggestion[] | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setExpanded(false);
    (async () => {
      try {
        const list = await suggestionsApi.get({
          sectionName,
          componentName,
          existingFindings: existingFindingTitles,
        });
        if (!cancelled) setRows(list);
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          onError?.((e as Error).message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [sectionName, componentName, existingFindingTitles, refreshKey, onError]);

  const sorted = useMemo(() => rows ?? [], [rows]);
  const capped = sorted.slice(0, EXPANDED_MAX);
  const visible = expanded ? capped : capped.slice(0, COLLAPSED_COUNT);
  const hiddenCount = Math.min(sorted.length, EXPANDED_MAX) - COLLAPSED_COUNT;

  if (rows === null) {
    return (
      <div className="dl-strip" aria-busy="true">
        <div className="dl-strip-head">
          <span className="dl-strip-title">Finding suggestions</span>
        </div>
        <div className="dl-sug-list">
          {(['80%', '65%', '72%'] as const).map((w, i) => (
            <div key={i} className="dl-sug-skeleton" style={{ width: w }} />
          ))}
        </div>
      </div>
    );
  }

  if (sorted.length === 0) return null;

  return (
    <div className="dl-strip">
      <div className="dl-strip-head">
        <span className="dl-strip-title">Suggestions</span>
      </div>

      <div className="dl-sug-list">
        {visible.map((row) => (
          <SuggestionRow
            key={row.id}
            row={row}
            onClick={() =>
              onSuggestionSelected({
                title: row.title,
                description: row.description,
                severity: row.severity,
              })
            }
          />
        ))}
      </div>

      {sorted.length > COLLAPSED_COUNT && (
        <button
          type="button"
          className="dl-strip-toggle dl-strip-toggle--list"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? (
            <>Show fewer <span style={{ display:'inline-flex', transform:'rotate(180deg)' }}><Icon name="chevDown" size={12} /></span></>
          ) : (
            <>Show {hiddenCount} more <Icon name="chevDown" size={12} /></>
          )}
        </button>
      )}
    </div>
  );
}

interface RowProps {
  row: AiSuggestion;
  onClick: () => void;
}

function SuggestionRow({ row, onClick }: RowProps) {
  const sev: EntrySeverity = row.severity;
  const dotClass =
    sev === 'safety_concern' ? 'dl-sug-dot--safety'
    : sev === 'recommendation' ? 'dl-sug-dot--rec'
    : 'dl-sug-dot--none';

  return (
    <button
      type="button"
      className="dl-sug-row"
      onClick={onClick}
      aria-label={`Use suggestion: ${row.title}`}
    >
      <span className={'dl-sug-dot ' + dotClass} aria-hidden="true" />
      <span className="dl-sug-title">{row.title}</span>
      {row.source === 'ai_generated' && (
        <span className="dl-chip-eyebrow" aria-hidden="true">AI</span>
      )}
    </button>
  );
}
